import { intl } from "LocaleWrapper";
import { Alert, Skeleton } from "@mui/material";
import WidgetCard from "view/dashboard/components/WidgetCard";
import WidgetMenu from "view/dashboard/components/WidgetMenu";
import { ResolveError, Widget, WidgetTemplate } from "view/dashboard/types/widget";
import { buildWidget } from "view/dashboard/utils/DashboardUtils";
import { SOURCES } from "view/dashboard/widgets/sources";
import { VISUALS } from "view/dashboard/widgets/visuals";

const REASON_KEYS: Record<ResolveError["reason"], string> = {
	"unknown-source": "WIDGET_ERROR_UNKNOWN_SOURCE",
	"unknown-visual": "WIDGET_ERROR_UNKNOWN_VISUAL",
	"kind-mismatch": "WIDGET_ERROR_TYPE_MISMATCH",
	"bad-config": "WIDGET_ERROR_BAD_CONFIG",
};

const WidgetContent = ({ widget }: { widget: Widget }) => {
	const { source, visual, sourceConfig, visualConfig } = widget;
	const { value, loading, error } = source.useValue(sourceConfig);
	const Render = visual.render;

	if (error) {
		return <Alert severity="error">{error.message}</Alert>;
	}

	if (loading || value === undefined) {
		return <Skeleton variant="rounded" height="100%" />;
	}

	return <Render value={value} config={visualConfig} />;
};

type WidgetGridProps = {
	widgets: Array<WidgetTemplate>;
	onEdit?: (template: WidgetTemplate) => void;
	onDelete?: (id: string) => void;
};

const WidgetGrid = ({ widgets, onEdit, onDelete }: WidgetGridProps) => {
	const sorted = [ ...widgets ].sort((a, b) => a.order - b.order);

	const actionFor = (template: WidgetTemplate) => onEdit && onDelete
		? <WidgetMenu
			onEdit={() => onEdit(template)}
			onDelete={() => onDelete(template.id)} />
		: undefined;

	return <div style={{
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
		gridAutoRows: "minmax(250px, auto)",
		gap: 16,
	}}>
		{sorted.map(template => {
			const widgetResult = buildWidget(template, SOURCES, VISUALS);

			return <WidgetCard
				key={template.id}
				url={template.config.url}
				title={template.config.label}
				action={actionFor(template)}>
				{!widgetResult.ok ? (
					<Alert severity="warning">{intl.formatMessage({ id: REASON_KEYS[widgetResult.reason] })}</Alert>
				) : (
					<WidgetContent key={template.source} widget={widgetResult.widget} />
				)}
			</WidgetCard>;
		})}
	</div>;
};

export default WidgetGrid;
